/*
 * GIFT PROMO MECHANIC — see docs/gift-promo.md
 *
 * Storefront half of the "choose your free gift" mechanic. Pricing is handled by a native
 * "Buy X Get Y" automatic discount in the Shopify admin; this module only:
 *   1. validates that a gift has been chosen before add-to-cart,
 *   2. adds the chosen gift as a second cart line carrying `_free_gift` / `_gift_for`,
 *   3. keeps that line honest on every cart update (one gift, quantity 1, parent still present).
 *
 * This file is only loaded when the mechanic is enabled OR the current cart still contains a
 * gift line (so stale carts are cleaned up after a promo ends). See snippets/gift-promo-init.liquid.
 */
(() => {
  'use strict';

  const GIFT_PROPERTY = '_free_gift';
  const PARENT_PROPERTY = '_gift_for';
  const FIELD_NAME = 'gift_variant_id';
  const SOURCE = 'gift-promo';

  const config = Object.assign(
    { enabled: false, qualifyingTag: '', giftVariantIds: [], hasGiftLine: false },
    window.giftPromoConfig || {}
  );

  const giftVariantIds = (config.giftVariantIds || []).map(String);

  let syncing = false;
  let resyncQueued = false;

  /* -------------------------------------------------------------- helpers */

  function isGiftLine(item) {
    return Boolean(item && item.properties && item.properties[GIFT_PROPERTY] === 'true');
  }

  function parentVariantOf(item) {
    const parent = item && item.properties && item.properties[PARENT_PROPERTY];
    return parent ? String(parent) : null;
  }

  function cartChangeUrl() {
    return (window.routes && window.routes.cart_change_url) || '/cart/change';
  }

  function cartAddUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add';
  }

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  function postJSON(url, body) {
    return fetch(`${url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => response.json());
  }

  /* ------------------------------------------------------ picker + validation */

  function pickerFor(form) {
    return form ? form.querySelector('[data-gift-picker]') : null;
  }

  function showPickerError(picker) {
    const errorEl = picker.querySelector('.gift-picker__error');
    const textEl = picker.querySelector('.gift-picker__error-text');
    const messageEl = picker.querySelector('[data-gift-validation-message]');
    if (!errorEl || !textEl) return;

    textEl.textContent = messageEl ? messageEl.textContent.trim() : 'Please choose your free gift';
    errorEl.removeAttribute('hidden');
    picker.classList.add('gift-picker--error');

    const firstInput = picker.querySelector('.gift-picker__input');
    if (firstInput) firstInput.focus();
  }

  function clearPickerError(picker) {
    const errorEl = picker.querySelector('.gift-picker__error');
    if (errorEl) errorEl.setAttribute('hidden', '');
    picker.classList.remove('gift-picker--error');
  }

  // Clear the validation message as soon as the customer makes a choice.
  document.addEventListener('change', (event) => {
    if (!event.target.matches || !event.target.matches('.gift-picker__input')) return;
    const picker = event.target.closest('[data-gift-picker]');
    if (picker) clearPickerError(picker);
  });

  /* ---------------------------------------------------------------- cart sync */

  /**
   * The cart surfaces that have to be re-rendered after the sync writes to the cart, mirroring
   * Dawn's own updateQuantity(). Dawn's onCartUpdate() — the path taken when an *external*
   * source publishes cartUpdate, which is what we are — only re-renders the line items on the
   * cart page, so the total, the checkout button and the header count would otherwise stay
   * stale until the customer refreshed. Built fresh each time because it is DOM-dependent:
   * on a product page none of these exist and the list is simply empty.
   */
  function cartSectionTargets() {
    const targets = [];

    const add = (elementId, sectionId, selector) => {
      if (document.getElementById(elementId) && sectionId) targets.push({ elementId, sectionId, selector });
    };

    const items = document.getElementById('main-cart-items');
    if (items) add('main-cart-items', items.dataset.id, '.js-contents');

    const footer = document.getElementById('main-cart-footer');
    if (footer) add('main-cart-footer', footer.dataset.id, '.js-contents');

    add('cart-icon-bubble', 'cart-icon-bubble', '.shopify-section');
    add('cart-live-region-text', 'cart-live-region-text', '.shopify-section');

    return targets;
  }

  /**
   * Paints the sections returned with the cart write, and applies the same empty-state toggles
   * Dawn applies, so a cart emptied by the sync looks exactly like one emptied by hand.
   */
  function repaint(cart) {
    if (!cart || !cart.sections) return;

    const isEmpty = cart.item_count === 0;
    document
      .querySelectorAll('cart-items, cart-drawer-items')
      .forEach((el) => el.classList.toggle('is-empty', isEmpty));
    const footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', isEmpty);
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('is-empty', isEmpty);

    cartSectionTargets().forEach(({ elementId, sectionId, selector }) => {
      const html = cart.sections[sectionId];
      if (!html) return;
      const host = document.getElementById(elementId);
      const target = host.querySelector(selector) || host;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (source) target.innerHTML = source.innerHTML;
    });
  }

  function applyChange(change) {
    const body = { id: change.id, quantity: change.quantity };
    const targets = cartSectionTargets();

    if (targets.length) {
      body.sections = targets.map((target) => target.sectionId);
      body.sections_url = window.location.pathname;
    }

    return postJSON(cartChangeUrl(), body);
  }

  /**
   * Enforces the gift rules against a cart payload. Resolves to the updated cart when a
   * mutation was made, or null when the cart was already correct.
   *
   * Rules (one gift per order):
   *   - mechanic disabled            -> remove every gift line
   *   - parent variant gone from cart -> remove that gift line
   *   - gift no longer in the collection -> remove that gift line
   *   - more than one valid gift line -> keep the most recently added, remove the rest
   *   - gift quantity != 1           -> reset to 1
   */
  function enforceRules(cart) {
    const items = (cart && cart.items) || [];
    const giftLines = items.filter(isGiftLine);
    if (giftLines.length === 0) return Promise.resolve(null);

    const changes = [];

    if (!config.enabled) {
      giftLines.forEach((line) => changes.push({ id: line.key, quantity: 0 }));
    } else if (giftVariantIds.length === 0) {
      // Enabled but the gift collection is empty, deleted or unreadable. The discount cannot
      // apply in that state, so leaving the line would charge the customer full price for it.
      console.warn('[gift-promo] No gift variants configured — removing gift lines to avoid charging for them.');
      giftLines.forEach((line) => changes.push({ id: line.key, quantity: 0 }));
    } else {
      const presentVariantIds = new Set(items.filter((item) => !isGiftLine(item)).map((item) => String(item.variant_id)));

      const validGifts = giftLines.filter((line) => {
        const parent = parentVariantOf(line);
        if (!parent || !presentVariantIds.has(parent)) return false;
        if (giftVariantIds.indexOf(String(line.variant_id)) === -1) return false;
        return true;
      });

      // Shopify prepends newly added lines, so the FIRST valid gift in cart order is the most
      // recently added one. Only reachable if a cart somehow ends up with several gift lines.
      const keeper = validGifts.length ? validGifts[0] : null;

      giftLines.forEach((line) => {
        if (line !== keeper) changes.push({ id: line.key, quantity: 0 });
      });

      if (keeper && keeper.quantity !== 1) changes.push({ id: keeper.key, quantity: 1 });
    }

    if (changes.length === 0) return Promise.resolve(null);

    // Line keys stay stable across changes, so these can safely run in sequence.
    return changes.reduce(
      (chain, change) => chain.then(() => applyChange(change)),
      Promise.resolve()
    );
  }

  function runSync(cartData) {
    // Never drop an update that lands mid-sync: queue one re-run instead. A queued run that
    // finds nothing to fix simply makes no requests, so this cannot spin.
    if (syncing) {
      resyncQueued = true;
      return Promise.resolve();
    }
    syncing = true;

    // Only a full cart can be trusted here. `/cart/add.js` called with an `items` array
    // resolves to just the lines it added ({ items, sections }) with no `item_count`, which
    // would look like a cart containing nothing but the gift — and the rules below would then
    // remove that gift for having no qualifying product. Anything that is not unmistakably a
    // whole cart is re-fetched.
    const isFullCart = cartData && Array.isArray(cartData.items) && typeof cartData.item_count === 'number';
    const source = isFullCart ? Promise.resolve(cartData) : fetch(`${cartUrl()}.js`).then((r) => r.json());

    return source
      .then((cart) => enforceRules(cart))
      .then((updatedCart) => {
        if (!updatedCart) return;
        // Dawn has already painted stale markup at this point. Repaint the cart surfaces
        // ourselves first, then publish so the drawer and any other subscriber follow.
        repaint(updatedCart);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: updatedCart });
        }
      })
      .catch((error) => {
        console.error('[gift-promo] Cart sync failed', error);
      })
      .finally(() => {
        syncing = false;
        if (resyncQueued) {
          resyncQueued = false;
          return runSync(null);
        }
      });
  }

  /* ------------------------------------------------------------- public API */

  window.GiftPromo = {
    /**
     * Returns false (and shows the inline message) when a picker is present with no choice made.
     * Returns true in every other case, including when no picker is rendered.
     */
    validateForm(form) {
      const picker = pickerFor(form);
      if (!picker) return true;

      const chosen = picker.querySelector('.gift-picker__input:checked');
      if (chosen) {
        clearPickerError(picker);
        return true;
      }

      showPickerError(picker);
      return false;
    },

    /**
     * Pulls the chosen gift variant id out of the form data and removes the field, so the
     * qualifying product is added exactly as stock Dawn would add it.
     */
    takeSelection(formData) {
      if (!formData || typeof formData.get !== 'function') return null;
      const value = formData.get(FIELD_NAME);
      formData.delete(FIELD_NAME);
      return value || null;
    },

    /**
     * Adds the chosen gift as its own cart line. Called BEFORE the qualifying product is added,
     * so no sections are requested here — the qualifying add is the last one and its response
     * is what Dawn renders. Resolves true on success, false on failure.
     */
    addGift({ giftVariantId, parentVariantId }) {
      const body = {
        items: [
          {
            id: Number(giftVariantId),
            quantity: 1,
            properties: {
              [GIFT_PROPERTY]: 'true',
              [PARENT_PROPERTY]: String(parentVariantId),
            },
          },
        ],
      };

      return postJSON(cartAddUrl(), body)
        .then((response) => {
          if (response && response.status) {
            console.error('[gift-promo] Gift could not be added', response);
            return false;
          }
          return true;
        })
        .catch((error) => {
          console.error('[gift-promo] Gift add request failed', error);
          return false;
        });
    },

    sync: runSync,
  };

  /* -------------------------------------------------------------- bootstrap */

  function bind() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === SOURCE) return;
        return runSync(event && event.cartData);
      });
    }

    // Catches stale carts: a gift left over from a finished promo, or a qualifying product
    // removed on a previous page view. Only worth a request when the cart actually holds a
    // gift line — otherwise there is nothing for the rules to act on.
    if (config.hasGiftLine) runSync(null);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
